/**
 * Canonical derivation of a Principal's stable user id from identity claims.
 * Callers pass candidates in precedence order (`sub` → `cognito:username` →
 * `email`). Shared by the API's actor middleware (`apps/api/src/cognito-actor.ts`)
 * and the SPA's ownership checks (the Dashboard's "My Projects"), so the two
 * sides can never derive different ids from the same token.
 *
 * The first present (non-blank) candidate is authoritative: it is trimmed,
 * lowercased and stripped to `[0-9a-z]`, then zero-padded to at least three
 * characters. A chosen candidate that normalises to nothing yields `undefined`
 * — there is deliberately no fallthrough to later candidates.
 */
export const deriveUserId = (candidates: ReadonlyArray<string | undefined>): string | undefined => {
  const raw = candidates.find(
    (candidate): candidate is string => candidate !== undefined && candidate.trim().length > 0,
  );
  const suffix =
    raw
      ?.trim()
      .toLowerCase()
      .replace(/[^0-9a-z]/g, '') ?? '';
  if (suffix.length === 0) return undefined;
  return `user-${suffix.padEnd(3, '0')}`;
};
